import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { Image } from '../../Image/Image';
import { PhotographersLink } from '../../responsive/atoms/PhotographersLink';
import { ImageCoverFlowWrapper } from './ImageCoverFlowWrapper';
import { theme } from '../../../domain/theme';
import { imageResizerService } from '../../../domain/services/imageResizerService';
import { ParagraphWrapper } from '../ParagraphWrapper';
import { ReadMoreByLines } from '../../responsive/molecules/ReadMoreByLines';

const aspects = { landscape: '16x9', portrait: '3x4', square: '1x1' };

// Calculate if all items have the same orientation
const calculateSameOrientation = images => {
  const orientations = {};
  images.forEach(card => {
    const orientation = imageResizerService.getOrientation(
      card.entity.image.entity.mediaImage
    );
    orientations[orientation] = (orientations[orientation] || 0) + 1;
  });
  return Object.values(orientations).filter(val => val > 0).length === 1;
};

const ImageCarouselInner = ({ data, isAboveTheFold }) => {
  const widthSize = data.widthSize || 'full-width';
  const { images, backgroundColor, heading, description, layout } = data;

  const fontColor = backgroundColor === 'black' ? 'white' : 'black';

  const styles = {
    captionWrapper: css`
      display: flex;
      flex-direction: column;
      gap: 20px;
      ${theme.mq.small_desktop} {
        padding-bottom: 32px;
      }
      h2 {
        ${theme.font.family('medium')};
        font-weight: 500;
      }
      p {
        margin-bottom: 0;
        color: ${fontColor};
        ${theme.font.family('regularWeb')};
        font-size: 16px;
        line-height: 1.5;
        letter-spacing: initial;
      }
    `,
    caption: css`
      a {
        color: inherit;
        text-decoration: underline;
      }
    `
  };
  const filteredImages = images.filter(
    card => !!card.entity?.image?.entity?.mediaImage?.url
  );
  const thumbnailMenuImages = filteredImages.map(card => card.entity?.image);

  // Calculate if all items have the same orientation
  const allHaveTheSameOrientation = calculateSameOrientation(filteredImages);

  const items = filteredImages.map(card => {
    const { image } = card.entity;
    const orientation = imageResizerService.getOrientation(
      image.entity.mediaImage
    );
    const aspect = aspects[orientation];

    const maxWidths = {
      landscape: allHaveTheSameOrientation
        ? {
            mobile: 727,
            tablet: 885,
            small_desktop: widthSize === 'full-width' ? 870 : 972,
            large_desktop: widthSize === 'full-width' ? 1306 : 972
          }
        : {
            mobile: 727,
            tablet: widthSize === 'full-width' ? 829 : 885,
            small_desktop: 870,
            large_desktop: widthSize === 'full-width' ? 1306 : 1080
          },
      portrait: {
        mobile: 540,
        tablet: 540,
        small_desktop: allHaveTheSameOrientation ? 540 : 368,
        large_desktop: allHaveTheSameOrientation ? 540 : 456
      },
      square: {
        mobile: 720,
        tablet: 720,
        small_desktop: allHaveTheSameOrientation ? 720 : 490,
        large_desktop: allHaveTheSameOrientation ? 720 : 608
      }
    };

    let title;
    if (!card.entity.hideTitle) {
      if (card.entity.titleOverride) {
        title = card.entity.titleOverride;
      } else if (
        image.entity.entityLabel &&
        !/http|gif|jpg|jpeg|png/i.test(image.entity.entityLabel)
      ) {
        title = image.entity.entityLabel;
      }
    }

    let caption = null;
    if (!card.entity.hideAbstract) {
      caption = card.entity.descriptionOverride?.value || image.entity.caption;
    }

    return {
      key: image.entity.id,
      orientation,
      image: image.entity,
      element: (
        <Image
          entity={image.entity}
          aspect={aspect}
          maxWidths={maxWidths[orientation]}
          loading={isAboveTheFold ? 'eager' : 'lazy'}
          fade={false}
          noImgStyle
          key={`element-${image.entity.id}`}
        />
      ),
      caption: (
        <div css={styles.captionWrapper} key={`caption-${image.entity.id}`}>
          {!!title && <h2>{title}</h2>}
          {!!caption && (
            <div css={styles.caption}>
              <ReadMoreByLines
                text={caption}
                maxLines={{
                  mobile: 5,
                  tablet: 5,
                  small_desktop: 3,
                  large_desktop: 3
                }}
              />
            </div>
          )}
          {!card.entity.hideCredit && (
            <PhotographersLink
              photographers={image.entity.photographers}
              color={{
                text: '#999',
                link: '#999'
              }}
              type="default"
            />
          )}
        </div>
      )
    };
  });
  return (
    <ImageCoverFlowWrapper
      items={items}
      allHaveTheSameOrientation={allHaveTheSameOrientation}
      thumbnailMenuImages={thumbnailMenuImages}
      widthSize={widthSize}
      backgroundColor={backgroundColor}
      heading={heading}
      description={description}
      layout={layout}
      isAboveTheFold={isAboveTheFold}
    />
  );
};
ImageCarouselInner.defaultProps = {
  isAboveTheFold: false
};

ImageCarouselInner.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  isAboveTheFold: PropTypes.bool
};

export const ImageCarousel = ParagraphWrapper(ImageCarouselInner);
